/**
 * 色板 UI 状态 —— 品牌色与开关**持久化到 localStorage**（下次打开还在），
 * 映射表（源色→目标色）与文档绑定，换文档时清空。
 */
import { reactive } from 'vue';
import { DEFAULT_BRAND_PACK, makeBrandColor, type BrandPack, type ColorMapping } from '@/core/palette';

const STORAGE_KEY = 'svg-edit-lite:palette';

export interface PaletteState {
  packs: BrandPack[];
  activePackId: string;
  /** 源色 → 目标色 */
  mapping: ColorMapping;
  /** 保留亮度关系（应用阶段生效） */
  keepLuminance: boolean;
  /** 当前打开了"选色悬浮框"的源色（点行右侧色块）；null = 没开 */
  pickerFor: string | null;
  /** 当前**悬停预览**（鼠标移入颜色行）的颜色；null = 没悬停。高亮完全由它驱动 */
  hover: string | null;
  /** 「应用映射」下拉是否展开 */
  menuOpen: boolean;
  hint: string;
}

export const palette = reactive<PaletteState>({
  packs: [DEFAULT_BRAND_PACK],
  activePackId: DEFAULT_BRAND_PACK.id,
  mapping: {},
  keepLuminance: true,
  pickerFor: null,
  hover: null,
  menuOpen: false,
  hint: '',
});

export const activePack = (): BrandPack =>
  palette.packs.find((pack) => pack.id === palette.activePackId) ?? (palette.packs[0] as BrandPack);

export const brandColors = (): BrandPack['colors'] => activePack().colors;

export function setHint(text: string): void {
  palette.hint = text;
}

export function addBrandColor(name: string, hex: string): void {
  const pack = activePack();
  const color = makeBrandColor(name, hex);
  if (pack.colors.some((c) => c.hex.toLowerCase() === color.hex.toLowerCase() && c.name === color.name)) return;
  pack.colors.push(color);
  savePaletteState();
}

export function removeBrandColor(id: string): void {
  const pack = activePack();
  pack.colors = pack.colors.filter((c) => c.id !== id);
  savePaletteState();
}

/** 给某个源色指定目标色（target = null 表示清除该条） */
export function setMapping(source: string, target: string | null): void {
  if (target === null) delete palette.mapping[source];
  else palette.mapping[source] = target;
}

export function clearMapping(): void {
  palette.mapping = {};
  palette.pickerFor = null;
  setHint('');
}

export function resetPaletteDocState(): void {
  clearMapping();
  palette.hover = null;
  palette.pickerFor = null;
  palette.menuOpen = false;
}

export function savePaletteState(): void {
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ packs: palette.packs, activePackId: palette.activePackId, keepLuminance: palette.keepLuminance }),
    );
  } catch {
    /* 隐私模式 / 存储被禁：忽略（不影响使用） */
  }
}

export function loadPaletteState(): void {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return;
    const data = JSON.parse(raw) as Partial<PaletteState>;
    if (Array.isArray(data.packs) && data.packs.length > 0) palette.packs = data.packs;
    if (typeof data.activePackId === 'string') palette.activePackId = data.activePackId;
    if (typeof data.keepLuminance === 'boolean') palette.keepLuminance = data.keepLuminance;
  } catch {
    /* 坏数据一律忽略，回到默认包 */
  }
}
